import { FC, useMemo } from 'react';

import { cleanupDataSiteDetails, cleanupDataSiteROIModel, DLMROI, getLastPathFromWorkflow } from '../../../core/doclabel';

import { FormikValues, Formik, Form } from 'formik';
import { pickBy } from 'lodash';
import { useQueryClient } from 'react-query';

import { doFormSubmit, processNoteValues } from '../../../../components/helpers';
import { SubmitButtonRow } from '../../../../components/layout';
import { StatusErrorMessage } from '../../../../components/widgets';

import { getApplicantResponseVars } from '../submit-response-modal/core/_helpers';
import { DLMConfirmationForm, DLMPhasANoteForm, DLMSubmitDocumentsForm, DLMSubmitSignedROIForm } from '../submit-response-modal/forms';

const skipFields = [
  'verdict',
  'workflow',
  'note',
  'noteAttachments',
];

type Props = {
  siteID: number
  siteState: string
  appID: number
  siteROIS: Array<DLMROI>
  workflow: string
  doClose: Function    
}

const SiteApplicationResponseForm: FC<Props> = ({siteID, siteState, siteROIS, appID, workflow, doClose}) => {
    
  const queryClient = useQueryClient();

  const { initValues, schema, submitFunc } = useMemo(() => {        

    return getApplicantResponseVars(siteState, workflow, undefined, undefined, siteROIS);

  }, [siteState, workflow, siteROIS]);


  const doSubmit = async (values: any, actions: FormikValues) => {

    const { setStatus, setSubmitting } = actions;

    if (! submitFunc) {
      setStatus('Undefined function call');
      setSubmitting(false);
      return;
    } 

    let payload = pickBy(values, (value, key) => (
      (value && (! (key.match(/AttachmentName$/i) || (skipFields.indexOf(key) > -1)) )
    )));
    
    processNoteValues(values, payload);

    // set standard fields
    Object.assign(payload, {
      applicationid: appID,
      siteApplicationid: siteID,
    })

    if ((siteState === 'Pending Inspection') && (workflow === 'Label5')) {
      cleanupDataSiteDetails(payload, workflow);
    }

    if (siteState === 'Applicant Sign') {        
      cleanupDataSiteROIModel(payload);            
      if (payload.hasOwnProperty('siteDetails')) delete payload.siteDetails;
    }

    if (siteState === 'Applicant Confirmation') {    
      if (payload.hasOwnProperty('siteDetails')) delete payload.siteDetails;
      Object.assign(payload, {approved : values?.verdict === 'approved' ? true : false});
    }

    await doFormSubmit(submitFunc, payload, setStatus, setSubmitting, () => {
      queryClient.invalidateQueries(['DLMAPPLICATION-LOAD', { dlmappid: appID } ]);
      queryClient.invalidateQueries(['DLMAPPLICATIONS-LIST', { lastpath: getLastPathFromWorkflow(workflow)} ]);
      queryClient.invalidateQueries(['DLMAPPLICATIONS-LIST', { listmode: 'own'} ]);                            
      doClose();          
    });
  }

  if (! initValues) {
    return null;
  }

  return(
  <Formik
    initialValues={initValues}
    validationSchema={schema}
    enableReinitialize={true}
    onSubmit={doSubmit}
  >
  {({status }) => (
  <Form id='kt_doclabel_siteapplication_submitresponse_form' className='form' noValidate>
    <div
      className='d-flex flex-column scroll-y me-n7 ps-2 pe-7'
      id='kt_doclabel_siteapplication_submitresponse_scroll'
      data-kt-scroll='true'
      data-kt-scroll-activate='{default: false, lg: true}'
      data-kt-scroll-max-height='auto'
      data-kt-scroll-dependencies='#kt_submit_review_header'
      data-kt-scroll-wrappers='#kt_doclabel_siteapplication_submitresponse_scroll'
      data-kt-scroll-offset='300px'
    >
      { (
        (
          siteState === 'Site Inspection - Issue' ||
          siteState === 'Program Manager Approval - Issue' ||                
          siteState === 'Component Check Issue' ||
          siteState === 'Component Check - Issue'
        )) &&              
      <DLMPhasANoteForm />                            
      }

      { (
        (
          siteState === 'Pending Inspection'
        ) && (
          workflow === 'Label5'
        )) &&
      <DLMSubmitDocumentsForm />
      }

      { ( siteState === 'Applicant Sign' ) &&
      <DLMSubmitSignedROIForm />
      }

      { ( siteState === 'Applicant Confirmation' ) &&
      <DLMConfirmationForm />
      }

      <StatusErrorMessage status={status} />
      <SubmitButtonRow doClose={doClose} />
    </div>
  </Form>
  )}
  </Formik>
  )

}

export {SiteApplicationResponseForm}
